import { Receipt, FileSignature, FileCheck2, FileX2 } from 'lucide-react';
import { useDashboard } from '@/hooks/useDashboard';
import { KpiCard } from '@/components/KpiCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { fmtEuro } from '@/lib/utils';
import { useIsTV } from '@/hooks/useDevice';

export function Pipeline() {
  const { data, isLoading } = useDashboard();
  const isTV = useIsTV();
  if (isLoading) return <Skeleton className="h-96" />;
  if (!data) return null;
  const p = data.pipeline;

  const size = isTV ? 'lg' : 'md';
  const excluded = p.excludedQuotes ?? [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          icon={<FileSignature className="h-3.5 w-3.5" />}
          label="Devis en attente signature"
          value={p.pendingQuotes.length}
          sub={fmtEuro(p.totalPendingQuotes)}
          tone="warning"
          size={size}
          hint="Devis envoyés en attente de signature client (Qonto, statut pending_approval). Tant que pas signé, pas garantis."
        />
        <KpiCard
          icon={<FileCheck2 className="h-3.5 w-3.5" />}
          label="Devis signés à facturer"
          value={p.approvedQuotes.length}
          sub={fmtEuro(p.totalApprovedQuotes)}
          tone="info"
          size={size}
          hint="Devis Qonto approuvés (signés) mais pas encore convertis en facture. Tant que ce n'est pas facturé et encaissé, l'argent ne rentre pas."
        />
        <KpiCard
          icon={<FileX2 className="h-3.5 w-3.5" />}
          label="Factures impayées"
          value={p.unpaidInvoices.length}
          sub={fmtEuro(p.totalUnpaid)}
          tone="danger"
          size={size}
          hint="Factures Qonto émises mais non encaissées. À relancer dès 30 jours d'impayé."
        />
        <KpiCard
          icon={<Receipt className="h-3.5 w-3.5" />}
          label="Factures du mois"
          value={`${p.invoicesThisMonth} / ${p.invoicesTarget ?? '—'}`}
          sub="Objectif mensuel"
          tone={p.invoicesTarget && p.invoicesThisMonth >= p.invoicesTarget ? 'success' : 'default'}
          size={size}
        />
      </div>

      {excluded.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">
              Écartés ({excluded.length}) — {fmtEuro(p.totalExcludedQuotes ?? 0)} sortis de l'encours, décision consignée
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-xs">
            {excluded.map((e) => (
              <div key={e.number} className="space-y-0.5">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-medium">[{e.libelle}]</span>
                  <span className="tabular-nums">{fmtEuro(e.total)}</span>
                  <span>{e.client} · <span className="font-mono">{e.number}</span></span>
                </div>
                <p className="text-muted-foreground">{e.motif}</p>
                <p className="text-muted-foreground">
                  → {e.suite}{e.decide_le ? ` (tranché le ${e.decide_le})` : ''}
                  {!e.dans_qonto && ' · absent de la réponse Qonto, montant lu dans la décision'}
                </p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Légende</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 text-xs text-muted-foreground">
          <p><strong>Devis en attente</strong> → envoyé au client, en attente de signature.</p>
          <p><strong>Devis signés</strong> → approuvés, à transformer en facture rapidement.</p>
          <p><strong>Factures impayées</strong> → émises mais non encaissées.</p>
          <p><strong>Écartés</strong> → tranchés par Lionel (annulé, déjà facturé, facturé au mois…) : hors de tous les totaux.</p>
          <p>Source : <strong>Qonto</strong> (facturation et devis, en direct via API).</p>
        </CardContent>
      </Card>
    </div>
  );
}
